import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { UserBadge } from '../components/UserBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Building2, Plus, Search, ExternalLink, Loader2, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const INDUSTRIES = ['Tecnologia Agricola','Agricoltura','Macchinari Agricoli','Distribuzione','Consulenza','Alimentare','Energia','Altro'];
const SIZES = ['1-10','11-50','51-200','201-500','500+'];
const SOURCES = ['Web','Referral','Fiera','Cold Call','Social','Altro'];

export default function Companies() {
  const navigate = useNavigate();
  const { canCreate, canDelete } = usePermissions();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    name:'', industry:'', size:'', lead_source:'', notes:'', tags:'',
    // Identificazione
    ragione_sociale:'', nome_commerciale:'', partita_iva:'', codice_fiscale:'', codice_cliente:'',
    // Sede
    indirizzo_sede_legale:'', cap:'', citta:'', provincia:'', stato:'Italia', indirizzo_sede_operativa:'',
    // Contatti
    phone:'', email:'', pec:'', website:'',
    // DDT
    ddt_destinazione_nome:'', ddt_destinazione_indirizzo:'', ddt_destinazione_cap_citta:'',
    ddt_causale_trasporto:'', ddt_aspetto_beni:'', ddt_trasporto_a_cura_di:'', ddt_vettore:'',
    // Categoria cliente (DDT type)
    customer_category:''
  };
  const [form, setForm] = useState(emptyForm);

  const fetchCompanies = async (q = '') => {
    try {
      const res = await axios.get(`${API}/companies`, { params: { search: q || undefined } });
      setCompanies(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento aziende');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompanies(); }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCompanies(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await axios.post(`${API}/companies`, { ...form, tags });
      toast.success('Azienda creata con successo');
      setShowForm(false);
      setForm(emptyForm);
      fetchCompanies(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}"?`)) return;
    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success('Azienda eliminata');
      setCompanies(c => c.filter(co => co._id !== id && co.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>Aziende</h1>
          <p className="text-muted-foreground text-sm mt-1">{companies.length} aziende nel database</p>
        </div>
        {canCreate('companies') && (
          <Button onClick={() => setShowForm(true)} data-testid="add-company-btn">
            <Plus size={16} className="mr-2" />
            Nuova Azienda
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Cerca aziende..."
          className="pl-9"
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="companies-search"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : companies.length === 0 ? (
        <div className="text-center py-16">
          <Building2 size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">Nessuna azienda</h3>
          <p className="text-muted-foreground text-sm">Inizia aggiungendo la prima azienda</p>
          {canCreate('companies') && (
            <Button className="mt-4" onClick={() => setShowForm(true)}>
              <Plus size={16} className="mr-2" />Aggiungi Azienda
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Azienda</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Settore</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Dimensione</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Contatti</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Fonte</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Azioni</th>
                </tr>
              </thead>
              <tbody className="stagger-children">
                {companies.map(co => {
                  const id = co._id || co.id;
                  return (
                    <tr
                      key={id}
                      className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => navigate(`/companies/${id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Building2 size={14} className="text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{co.name}</p>
                            {co.codice_cliente && <p className="text-xs text-muted-foreground font-mono">Cod: {co.codice_cliente}</p>}
                            {!co.codice_cliente && co.email && <p className="text-xs text-muted-foreground">{co.email}</p>}
                            <UserBadge userId={co.assigned_to} size="xs" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-muted-foreground">{co.industry || '-'}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {co.size ? <Badge variant="outline">{co.size}</Badge> : '-'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-muted-foreground">{co.contacts_count || 0}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-muted-foreground text-xs">{co.lead_source || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/companies/${id}`)} data-testid={`view-company-${id}`}>
                            <ExternalLink size={13} />
                          </Button>
                          {canDelete('companies') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(id, co.name)} data-testid={`delete-company-${id}`}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Manrope, sans-serif'}}>Nuova Azienda</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-6 mt-2">

            {/* Sezione 1: Identificazione Azienda */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Identificazione Azienda</p>
              <div className="space-y-1.5">
                <Label>Nome azienda *</Label>
                <Input value={form.name} onChange={e => setField('name', e.target.value)} required data-testid="company-name-input" placeholder="Nome visualizzato nell'app" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Ragione Sociale</Label>
                  <Input value={form.ragione_sociale} onChange={e => setField('ragione_sociale', e.target.value)} placeholder="es. ACME S.r.l." data-testid="company-ragione-sociale" />
                </div>
                <div className="space-y-1.5">
                  <Label>Nome Commerciale / Brand</Label>
                  <Input value={form.nome_commerciale} onChange={e => setField('nome_commerciale', e.target.value)} placeholder="es. ACME" data-testid="company-nome-commerciale" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Partita IVA</Label>
                  <Input value={form.partita_iva} onChange={e => setField('partita_iva', e.target.value)} placeholder="IT12345678901" data-testid="company-partita-iva" />
                </div>
                <div className="space-y-1.5">
                  <Label>Codice Fiscale</Label>
                  <Input value={form.codice_fiscale} onChange={e => setField('codice_fiscale', e.target.value)} placeholder="12345678901" data-testid="company-codice-fiscale" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Codice Cliente</Label>
                  <Input value={form.codice_cliente} onChange={e => setField('codice_cliente', e.target.value)} placeholder="es. CLI001" data-testid="company-codice-cliente-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Settore</Label>
                  <Select value={form.industry} onValueChange={v => setField('industry', v)}>
                    <SelectTrigger data-testid="company-industry-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Dimensione</Label>
                  <Select value={form.size} onValueChange={v => setField('size', v)}>
                    <SelectTrigger><SelectValue placeholder="Dipendenti..." /></SelectTrigger>
                    <SelectContent>{SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Fonte lead</Label>
                  <Select value={form.lead_source} onValueChange={v => setField('lead_source', v)}>
                    <SelectTrigger><SelectValue placeholder="Come ci hanno trovato?" /></SelectTrigger>
                    <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">
                    Categoria Cliente
                    <span className="text-[10px] text-muted-foreground font-normal">(determina il tipo di DDT)</span>
                  </Label>
                  <Select value={form.customer_category || 'none'} onValueChange={v => setField('customer_category', v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="company-category-select"><SelectValue placeholder="Non classificato (default GDO)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non classificato (default GDO)</SelectItem>
                      <SelectItem value="gdo">GDO — DDT senza prezzi</SelectItem>
                      <SelectItem value="distributore">Distributore — DDT con prezzi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tag (separati da virgola)</Label>
                  <Input value={form.tags} onChange={e => setField('tags', e.target.value)} placeholder="premium, priorità..." />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Note</Label>
                <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} />
              </div>
            </div>

            {/* Sezione 2: Sede e Indirizzi */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Sede e Indirizzi</p>
              <div className="space-y-1.5">
                <Label>Indirizzo Sede Legale</Label>
                <Input value={form.indirizzo_sede_legale} onChange={e => setField('indirizzo_sede_legale', e.target.value)} placeholder="Via..." data-testid="company-indirizzo-sede-legale" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>CAP</Label>
                  <Input value={form.cap} onChange={e => setField('cap', e.target.value)} placeholder="00100" data-testid="company-cap" />
                </div>
                <div className="space-y-1.5">
                  <Label>Città</Label>
                  <Input value={form.citta} onChange={e => setField('citta', e.target.value)} placeholder="Roma" data-testid="company-citta" />
                </div>
                <div className="space-y-1.5">
                  <Label>Provincia</Label>
                  <Input value={form.provincia} onChange={e => setField('provincia', e.target.value)} placeholder="RM" maxLength={2} data-testid="company-provincia" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Stato</Label>
                  <Input value={form.stato} onChange={e => setField('stato', e.target.value)} placeholder="Italia" data-testid="company-stato" />
                </div>
                <div className="space-y-1.5">
                  <Label>Indirizzo Sede Operativa</Label>
                  <Input value={form.indirizzo_sede_operativa} onChange={e => setField('indirizzo_sede_operativa', e.target.value)} placeholder="Se diversa dalla sede legale" data-testid="company-indirizzo-operativa" />
                </div>
              </div>
            </div>

            {/* Sezione 3: Contatti */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Contatti</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Telefono</Label>
                  <Input value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="+39 06 1234567" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email generale</Label>
                  <Input type="email" value={form.email} onChange={e => setField('email', e.target.value)} placeholder="info@azienda.it" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>PEC</Label>
                  <Input type="email" value={form.pec} onChange={e => setField('pec', e.target.value)} placeholder="azienda@pec.it" data-testid="company-pec" />
                </div>
                <div className="space-y-1.5">
                  <Label>Sito web</Label>
                  <Input value={form.website} onChange={e => setField('website', e.target.value)} placeholder="https://..." />
                </div>
              </div>
            </div>

            {/* Sezione 4: Dati Trasporto DDT */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Dati Trasporto DDT</p>
              <p className="text-xs text-muted-foreground -mt-1">Questi dati verranno pre-compilati automaticamente nella creazione degli ordini.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Luogo di Destinazione</Label>
                  <Input value={form.ddt_destinazione_nome} onChange={e => setField('ddt_destinazione_nome', e.target.value)} placeholder="es. Magazzino Nord" data-testid="company-ddt-destinazione-nome" />
                </div>
                <div className="space-y-1.5">
                  <Label>Indirizzo</Label>
                  <Input value={form.ddt_destinazione_indirizzo} onChange={e => setField('ddt_destinazione_indirizzo', e.target.value)} placeholder="Via..." data-testid="company-ddt-indirizzo" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>CAP / Città</Label>
                  <Input value={form.ddt_destinazione_cap_citta} onChange={e => setField('ddt_destinazione_cap_citta', e.target.value)} placeholder="00100 Roma" data-testid="company-ddt-cap-citta" />
                </div>
                <div className="space-y-1.5">
                  <Label>Causale Trasporto</Label>
                  <Input value={form.ddt_causale_trasporto} onChange={e => setField('ddt_causale_trasporto', e.target.value)} placeholder="es. Vendita" data-testid="company-ddt-causale" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Aspetto Esteriore Beni</Label>
                  <Input value={form.ddt_aspetto_beni} onChange={e => setField('ddt_aspetto_beni', e.target.value)} placeholder="es. Colli" data-testid="company-ddt-aspetto" />
                </div>
                <div className="space-y-1.5">
                  <Label>Trasporto a cura di</Label>
                  <Input value={form.ddt_trasporto_a_cura_di} onChange={e => setField('ddt_trasporto_a_cura_di', e.target.value)} placeholder="es. Mittente" data-testid="company-ddt-trasporto-cura" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Vettore</Label>
                <Input value={form.ddt_vettore} onChange={e => setField('ddt_vettore', e.target.value)} placeholder="es. BRT, GLS..." data-testid="company-ddt-vettore" />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-company-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva Azienda
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
